import { Character, CharacterNameValues, CharacterData } from '../Character';
import { VehicleSkinsValues } from '../CharacterSpine';
import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { Driver as Driver, DriverProfile, Vehicle, VehicleDetails } from '../Vehicle';
import { LevelManager } from './LevelManager';
import { VehicleManager } from './VehicleManager';

export class VehicleFactory {
  protected scene: Phaser.Scene;
  protected levelManager: LevelManager;
  protected vehicleManager: VehicleManager;
  protected matterUpdater: MatterPhysicsUpdater;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected gameUILayer: Phaser.GameObjects.Layer;
  protected deltaStepping: number;

  constructor(
    scene: Phaser.Scene,
    levelManager: LevelManager,
    vehicleManager: VehicleManager,
    matterUpdater: MatterPhysicsUpdater,
    vehicleLayer: Phaser.GameObjects.Layer,
    gameUILayer: Phaser.GameObjects.Layer,
    baseAIProfile?: DriverProfile
  ) {
    this.scene = scene;
    this.levelManager = levelManager;
    this.vehicleManager = vehicleManager;
    this.matterUpdater = matterUpdater;
    this.vehicleLayer = vehicleLayer;
    this.gameUILayer = gameUILayer;
    this.deltaStepping = this.matterUpdater.TimeStep;
  }

  public makePlayerProfile(skin: VehicleSkinsValues, upgrades: CharacterData): DriverProfile {
    return {
      driver: Driver.PlayerTaxi,
      skin: skin,
      accelerationPerMS: 0.0055 + upgrades.accelerationUpgrades * Character.accelerationValue,
      decelerationPerMS: 0.01,
      topSpeedPerMS: 0.8 + upgrades.topSpeedUpgrades * Character.topSpeedValue,
      topBoostSpeedPerMS: 0.5,
      maxSpeedPerMS: 1 + upgrades.topSpeedUpgrades * Character.topSpeedValue,
      minLaneChangeSpeed: 2,
      boostSpeedDecayRatePerMS: 0.006,
      playerDistanceMultiplierAhead: 1,
      playerDistanceMultiplierBehind: 1,
      playerMinDistanceAhead: 1000,
      playerMaxDistanceAhead: 5000,
      playerMinDistanceBehind: 1000,
      playerMaxDistanceBehind: 5000,
      passengerStealSpeedPerMS: 100 / 1000,
      passengerStealDecayRatePerMS: 30 / 500,
      passengerStealDefence: 2000
    };
  }

  public makeAITaxiProfile(skin: VehicleSkinsValues, upgrades: CharacterData): DriverProfile {
    return {
      driver: Driver.NPCTaxi,
      skin: skin,
      accelerationPerMS: 0.0055 + upgrades.accelerationUpgrades * Character.accelerationValue,
      decelerationPerMS: 0.01,
      topSpeedPerMS: 0.7 + upgrades.topSpeedUpgrades * Character.topSpeedValue,
      topBoostSpeedPerMS: 0.75,
      maxSpeedPerMS: 1.2 + upgrades.topSpeedUpgrades * Character.topSpeedValue,
      minLaneChangeSpeed: 2,
      boostSpeedDecayRatePerMS: 0.006,
      playerDistanceMultiplierAhead: 2,
      playerDistanceMultiplierBehind: 4,
      playerMinDistanceAhead: 500,
      playerMaxDistanceAhead: 5000,
      playerMinDistanceBehind: 250,
      playerMaxDistanceBehind: 5000,
      passengerStealSpeedPerMS: 80 / 1000,
      passengerStealDecayRatePerMS: 30 / 500,
      passengerStealDefence: 2000
    };

    // return {
    //   driver: Driver.NPCTaxi,
    //   skin: skin,
    //   accelerationPerMS: 0.0035 * this.deltaStepping,
    //   topSpeedPerMS: 0.5 * this.deltaStepping,
    //   topBoostSpeedPerMS: 0.5 * this.deltaStepping,
    //   maxSpeedPerMS: 0.75 * this.deltaStepping,
    //   boostSpeedDecayRatePerMS: 0.006,
    //   playerDistanceMultiplier: 2,
    //   playerMinDistance: 1000,
    //   playerMaxDistance: 5000,
    //   passengerStealSpeedPerMS: (100 / 1000) * this.deltaStepping,
    //   passengerStealDecayRatePerMS: (30 / 1000) * this.deltaStepping,
    //   passengerStealDefence: 0
    // };

    // let vehicleProfile1: DriverProfile = {
    //   driver: Driver.AITaxi,
    //   skin: VehicleSkins.TaxiJerry,
    //   acceleration: 0.005 * this.matterUpdater.TimeStep,
    //   topSpeed: 0.5 * this.matterUpdater.TimeStep,
    //   topBoostSpeed: 1 * this.matterUpdater.TimeStep,
    //   maxSpeed: 0.6 * this.matterUpdater.TimeStep,
    //   boostSpeedDecayRate: 0.1,
    //   passengerStealSpeed: (50 / 1000) * this.matterUpdater.TimeStep,
    //   passengerStealDecayRate: (30 / 1000) * this.matterUpdater.TimeStep,
    //   passengerStealDefence: 0
    // };

    // let vehicleProfile2: DriverProfile = {
    //   driver: Driver.AITaxi,
    //   skin: VehicleSkins.TaxiSpike,
    //   acceleration: 0.01 * this.matterUpdater.TimeStep,
    //   topSpeed: 0.5 * this.matterUpdater.TimeStep,
    //   topBoostSpeed: 0.5 * this.matterUpdater.TimeStep,
    //   maxSpeed: 0.6 * this.matterUpdater.TimeStep,
    //   boostSpeedDecayRate: 0.1,
    //   passengerStealSpeed: (50 / 1000) * this.matterUpdater.TimeStep,
    //   passengerStealDecayRate: (30 / 1000) * this.matterUpdater.TimeStep,
    //   passengerStealDefence: 0
    // };
  }

  public makeTrafficProfile(skin: VehicleSkinsValues): DriverProfile {
    return {
      driver: Driver.Civilian,
      skin: skin,
      accelerationPerMS: 0.0035,
      decelerationPerMS: 0.01,
      topSpeedPerMS: Phaser.Math.Between(21, 60) / 100,
      topBoostSpeedPerMS: 0,
      maxSpeedPerMS: 0.6,
      minLaneChangeSpeed: 2,
      boostSpeedDecayRatePerMS: 0.05,
      playerDistanceMultiplierAhead: 1,
      playerDistanceMultiplierBehind: 1,
      playerMinDistanceAhead: 1000,
      playerMaxDistanceAhead: 5000,
      playerMinDistanceBehind: 1000,
      playerMaxDistanceBehind: 5000,
      passengerStealSpeedPerMS: 0,
      passengerStealDecayRatePerMS: 0,
      passengerStealDefence: 0
    };

    // return {
    //   driver: Driver.Civilian,
    //   skin: skin,
    //   accelerationPerMS: 0.0035 * this.deltaStepping,
    //   topSpeedPerMS: (Phaser.Math.Between(10, 60) / 100) * this.deltaStepping,
    //   topBoostSpeedPerMS: 0 * this.deltaStepping,
    //   maxSpeedPerMS: 0.6 * this.deltaStepping,
    //   boostSpeedDecayRatePerMS: 0.05,
    //   playerDistanceMultiplier: 1,
    //   playerMinDistance: 1000,
    //   playerMaxDistance: 5000,
    //   passengerStealSpeedPerMS: 0,
    //   passengerStealDecayRatePerMS: 0,
    //   passengerStealDefence: 0
    // };
  }

  public makeVehicle(profile: DriverProfile, startLane: number, indentX: number): Vehicle {
    let vehicleDetails: VehicleDetails = {
      vehicleLayer: this.vehicleLayer,
      gameUILayer: this.gameUILayer,
      startIndentX: indentX,
      startLane: startLane,
      endPointX: this.levelManager.LevelEndPointX
    };

    let vehicle = new Vehicle(this.scene, this.vehicleManager, profile, this.levelManager.CurrentTheme.roadDetails, vehicleDetails);
    this.vehicleManager.registerVehicle(vehicle);
    this.vehicleLayer.add(vehicle);
    this.matterUpdater.add(vehicle.physicsUpdate, vehicle, 0);

    return vehicle;
  }
}
